import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Block, BlockOutlined } from '@mui/icons-material';
import { blockUser, unblockUser } from '../../services/follow-service';

interface BlockButtonProps {
  targetUserId: string;
  targetUsername: string;
  isBlocked: boolean;
  onBlockChange: (isBlocked: boolean) => void;
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
}

const BlockButton: React.FC<BlockButtonProps> = ({
  targetUserId,
  targetUsername,
  isBlocked,
  onBlockChange,
  size = 'medium',
  variant = 'outlined',
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBlockAction = async () => {
    if (isBlocked) {
      // Unblock user directly without confirmation
      await handleUnblock();
    } else {
      // Show confirmation dialog for blocking
      setConfirmDialogOpen(true);
    }
  };

  const handleBlock = async () => {
    setLoading(true);
    setError(null);
    setConfirmDialogOpen(false);

    try {
      await blockUser(targetUserId);
      onBlockChange(true);
    } catch (error: any) {
      console.error('Failed to block user:', error);
      setError(error.message || 'Failed to block user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async () => {
    setLoading(true);
    setError(null);

    try {
      await unblockUser(targetUserId);
      onBlockChange(false);
    } catch (error: any) {
      console.error('Failed to unblock user:', error);
      setError(error.message || 'Failed to unblock user');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        color={isBlocked ? 'primary' : 'error'}
        startIcon={
          loading ? (
            <CircularProgress size={16} />
          ) : isBlocked ? (
            <BlockOutlined />
          ) : (
            <Block />
          )
        }
        onClick={handleBlockAction}
        disabled={disabled || loading}
        sx={{
          minWidth: 100,
          textTransform: 'none',
        }}
      >
        {loading ? 'Processing...' : isBlocked ? 'Unblock' : 'Block'}
      </Button>

      {/* Block Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="block-dialog-title"
        aria-describedby="block-dialog-description"
      >
        <DialogTitle id="block-dialog-title">
          Block @{targetUsername}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="block-dialog-description">
            When you block someone:
            <br />
            • They won't be able to follow you or see your outfits
            <br />
            • You won't see their outfits in your feed
            <br />
            • Any existing follow relationships will be removed
            <br />
            <br />
            You can unblock them at any time from your privacy settings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleBlock}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Block />}
          >
            {loading ? 'Blocking...' : 'Block'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BlockButton;